/**
 * Ledger transport — the fiscal calendar, FX rates and the trial balance.
 *
 * Three contract facts that shape every caller:
 *
 *   • A fiscal period transition is a compare-and-set. The request carries
 *     `expected_status` as well as `target_status`, so a stale screen cannot
 *     close a period someone else already closed, and the reason is a
 *     controlled code — free text is not accepted.
 *   • A manual FX rate is always quote-per-base and always carries a reason
 *     code. There is no inverse-pair form of the operation.
 *   • The trial balance is cumulative as of a period end, not activity within
 *     it, and it is always company-wide — the contract publishes no branch
 *     filter, so no screen may imply one.
 */
import type { components } from "@erp/api-client";

import { apiFetch, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

export type FiscalYear = Schemas["FiscalYear"];
export type FiscalPeriod = Schemas["FiscalPeriod"];
export type FiscalPeriodStatus = Schemas["FiscalPeriodStatus"];
export type FiscalTransitionReason = Schemas["FiscalPeriodTransitionRequest"]["reason_code"];
export type FxRate = Schemas["FxRate"];
export type FxRateType = Schemas["FxRateType"];
export type ManualFxRateCreate = Schemas["ManualFxRateCreate"];
export type ManualFxReason = Schemas["ManualFxRateReasonCode"];
export type TrialBalance = Schemas["TrialBalance"];

export const FISCAL_PERIOD_STATUSES: readonly FiscalPeriodStatus[] = [
  "open",
  "soft_closed",
  "locked",
];

export const FX_RATE_TYPES: readonly FxRateType[] = ["daily", "month_end", "manual"];

export const MANUAL_FX_REASONS: readonly ManualFxReason[] = [
  "market_source_unavailable",
  "contract_rate",
  "correction",
  "management_override",
  "other_controlled",
];

/** The transitions this screen offers, and the reason each one is recorded
 *  under. Anything else the contract allows is a back-office migration, not a
 *  control an operator should reach for. */
export const TRANSITIONS: readonly {
  from: FiscalPeriodStatus;
  to: FiscalPeriodStatus;
  reason: FiscalTransitionReason;
}[] = [
  { from: "open", to: "soft_closed", reason: "routine_close" },
  { from: "soft_closed", to: "locked", reason: "period_lock" },
  { from: "soft_closed", to: "open", reason: "authorized_reopen" },
  { from: "locked", to: "soft_closed", reason: "controlled_correction" },
];

// ── Fiscal calendar ──────────────────────────────────────────────────────

/** `listFiscalYears` — GET · ledger.fiscal-calendar.read. Each year carries
 *  its periods, so the screen needs no second read per year. The endpoint
 *  publishes no sort, and a calendar out of chronological order is a puzzle,
 *  so the years are ordered here — by start date, which is the only ordering a
 *  calendar has. */
export async function listFiscalYears(signal?: AbortSignal): Promise<FiscalYear[]> {
  const page = await apiFetch<Page<FiscalYear>>("/fiscal-years", { query: { limit: 200 }, signal });
  return [...page.items].sort((left, right) => left.starts_on.localeCompare(right.starts_on));
}

/** `generateFiscalYear` — POST · ledger.fiscal-calendar.generate · client-key. */
export function generateFiscalYear(
  body: { code: string; starts_on: string },
  idempotencyKey: string,
): Promise<FiscalYear> {
  return apiFetch<FiscalYear>("/fiscal-years/generate", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

/** `transitionFiscalPeriod` — POST · ledger.fiscal-period.transition ·
 *  client-key. `expected_status` is the compare half of a compare-and-set. */
export function transitionFiscalPeriod(
  fiscalPeriodId: string,
  body: {
    expected_status: FiscalPeriodStatus;
    target_status: FiscalPeriodStatus;
    reason_code: FiscalTransitionReason;
  },
  idempotencyKey: string,
): Promise<FiscalPeriod> {
  return apiFetch<FiscalPeriod>(
    `/fiscal-periods/${encodeURIComponent(fiscalPeriodId)}/transitions`,
    { method: "POST", body, idempotencyKey },
  );
}

// ── FX rates ─────────────────────────────────────────────────────────────

export interface FxRateFilters {
  base_ccy?: string;
  quote_ccy?: string;
  rate_type?: FxRateType;
  valid_from?: string;
  valid_to?: string;
}

/** `listFxRates` — GET · ledger.fx-rate.read. */
export function listFxRates(
  filters: FxRateFilters,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<Page<FxRate>> {
  return apiFetch<Page<FxRate>>("/fx-rates", {
    query: { ...filters, limit: 100, ...(cursor === undefined ? {} : { cursor }) },
    signal,
  });
}

/** `createManualFxRate` — POST · ledger.fx-rate.manual.create · client-key.
 *  Ingested rates come from a provider feed; this is the human exception, and
 *  it is recorded as one. */
export function createManualFxRate(
  input: ManualFxRateCreate,
  idempotencyKey: string,
): Promise<FxRate> {
  return apiFetch<FxRate>("/fx-rates/manual", { method: "POST", body: input, idempotencyKey });
}

// ── Trial balance ────────────────────────────────────────────────────────

/** `getTrialBalance` — GET · ledger.trial-balance.read. Cumulative closing
 *  balances as of the period's end, company-wide. */
export function getTrialBalance(
  fiscalPeriodId: string,
  options: { currency?: string; include_zero?: boolean } = {},
  signal?: AbortSignal,
): Promise<TrialBalance> {
  return apiFetch<TrialBalance>("/reports/trial-balance", {
    query: {
      fiscal_period_id: fiscalPeriodId,
      ...(options.currency === undefined ? {} : { currency: options.currency }),
      ...(options.include_zero === true ? { include_zero: true } : {}),
    },
    signal,
  });
}
